import { _ } from "../../../polyglot/polyglot.js";
import { helpIconRenderer } from "../../makeRoadmapStepContent.js";
import { makeCancelAndOkFooterBuilder } from "../../../widgets/dialogs/footers/footers.js";
import AbstractRoadmapStep from "../../AbstractRoadmapStep.js";
import AccountPayableAndReceivableDelayStrategy from "../AccountPayableAndReceivableDelayStrategy/AccountPayableAndReceivableDelayStrategy.js";
import NewDialog from "../../../widgets/NewDialog/NewDialog.js";
import percentage from "../../../percentage.js";
import WidgetsListWidget from "../../../widgets/WidgetsListWidget/WidgetsListWidget.js";

/**
 * `AbstractPayablesAndReceivablesStep` is a step designed
 *  to contain the shared logic of those subclasses implementing
 *  delay payment configurations.
 *
 * @abstract
 */
export default class AbstractPayablesAndReceivablesStep extends AbstractRoadmapStep {
	_getReceivableDelays() {
		this._subclassResponsibility();
	}

	_setReceivableDelays() {
		this._subclassResponsibility();
	}

	_getPayableDelays() {
		this._subclassResponsibility();
	}

	_setPayableDelays() {
		this._subclassResponsibility();
	}

	_getReceivableDelayDialog() {
		if (this._receivableDelayDialog) {
			return this._receivableDelayDialog;
		}

		this._receivableDelayDialog = this._makeDelayDialog({
			title: _("Accounts receivable"),
			accessor: () => this._getReceivableDelays(),
			mutator: (value) => {
				this._setReceivableDelays(value);
				const delays = this._formatDelays(value);
				this._receivableDelays.setContent(delays);
			},
		});

		return this._receivableDelayDialog;
	}

	_getPayableDelayDialog() {
		if (this._payableDelayDialog) {
			return this._payableDelayDialog;
		}

		this._payableDelayDialog = this._makeDelayDialog({
			title: _("Accounts payable"),
			accessor: () => this._getPayableDelays(),
			mutator: (value) => {
				this._setPayableDelays(value);
				const delays = this._formatDelays(value);
				this._payableDelays.setContent(delays);
			},
		});

		return this._payableDelayDialog;
	}

	_makeDelayDialog({ title, accessor, mutator }) {
		let values = [...accessor()];

		const widget = new WidgetsListWidget({
			strategy: new AccountPayableAndReceivableDelayStrategy({
				items: values,
				onValuesChange: (delays) => (values = delays),
			}),
		});

		return new NewDialog({
			title,
			body: widget,
			contentCssClass: "resizable-modal-content",
			footerBuilder: makeCancelAndOkFooterBuilder({
				okLabel: _("Save changes"),
				callback: ({ hide }) => {
					if (!widget.validate()) {
						return;
					}
					mutator(values);
					hide();
				},
			}),
		});
	}

	_formatDelays(delays) {
		return delays
			.map((amount, index) =>
				_("{value} month {monthIndex}", {
					value: percentage(amount / 100),
					monthIndex: index + 1,
				}),
			)
			.join(", ");
	}

	_actionRenderer({
		label,
		value,
		linkLabel,
		click,
		helpText,
		dataTestName,
	}) {
		const link = (html) =>
			html.span(
				html.a(
					{ class: "link", click, "data-test": "link" },
					linkLabel,
				),
				helpIconRenderer({ helpText }),
			);

		return (html) =>
			html.div(
				{ class: "action", "data-test": dataTestName },
				html.div({ class: "action-label" }, label),
				value,
				link,
			);
	}

	_missesDelays(delays) {
		return delays.length === 1 && delays[0] === 0;
	}

	_raiseBlankErrorForContent(content) {
		content.setContent(this._renderBlankErrorOn);
		this.addSubmitError(_("Can not be blank"));
	}

	_renderBlankErrorOn(html) {
		html.div({ class: "error" }, _("Can not be blank"));
	}
}
